import { FormContext } from "@common/contexts/Form";
import { DynamicMultiSelect } from "@common/controls/Inputs/Select/MultiSelect/component";
import { DynamicSelect } from "@common/controls/Inputs/Select/component";
import { useI18n } from "@common/hooks/i18n";
import apiV1 from "@common/lib/api";
import { usePromise } from "@common/lib/hooks";
import getOptions from "@common/lib/options";
import React from "react";
import FormField, { type FormFieldProps } from "./component";

type RegionFormFieldFieldProps = Omit<FormFieldProps<string | null, any>, "Component" | "componentProps"> & {
  isMultiselect: boolean;
};
type Subdivision = {
  countryCode: string;
  subdivisionCode: string;
  label: string;
};
const RegionFormField = ({ isMultiselect, style, ...props }: RegionFormFieldFieldProps) => {
  const i18n = useI18n();
  const context = React.useContext(FormContext);

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const [countryCode, setCountryCode] = React.useState<string | null>(null);
  const [region, isLoadingRegion] = usePromise<Subdivision>({
    fn: (value) => apiV1.get(i18n, `/system/subdivisions/${value}`),
    args: [props.value],
    shouldLoad: !!props.value
  });

  React.useEffect(() => {
    region && setCountryCode(region.countryCode);
  }, [region]);

  const onChange = React.useCallback(
    (name: string, value: any) => {
      if (name === "countryCode") {
        if (value !== countryCode) {
          setCountryCode(value);
          (props.onChange ?? context.onChange)(props.name, null); // reset value when country changes
        }
      } else {
        (props.onChange ?? context.onChange)(name, value);
      }
    },
    [props.onChange, context.onChange]
  );

  const colSpan = props.colSpan ?? 12;
  const countryColSpan = Math.floor(colSpan / 2);
  const regionColSpan = colSpan - countryColSpan;

  const fetchRegionOptions = React.useCallback(async () => {
    const regions = await options.regions(countryCode);
    return regions.map((x) => ({ ...x, group: undefined }));
  }, [countryCode]);

  return (
    <>
      <FormField
        label={props.label}
        colSpan={countryColSpan}
        name="countryCode"
        Component={DynamicSelect}
        componentProps={{
          fetchOptions: options.countries,
          isLoading: isLoadingRegion,
          sort: "asc"
        }}
        onChange={onChange}
        value={countryCode}
      />
      {isMultiselect ? (
        <FormField
          {...props}
          colSpan={regionColSpan}
          Component={DynamicMultiSelect}
          componentProps={{
            dependent: countryCode,
            fetchOptions: fetchRegionOptions,
            isLoading: isLoadingRegion,
            sort: "asc"
          }}
          label={props.label ? "" : undefined}
          onChange={onChange}
        />
      ) : (
        <FormField
          {...props}
          colSpan={regionColSpan}
          Component={DynamicSelect}
          componentProps={{
            dependent: countryCode,
            fetchOptions: fetchRegionOptions,
            isLoading: isLoadingRegion,
            sort: "asc"
          }}
          label={props.label ? "" : undefined}
          onChange={onChange}
        />
      )}
    </>
  );
};

export default RegionFormField;
